import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import {
  LayoutDashboard, Users, Building2, Wrench, MapPin, FileText,
  PenTool, Settings, Image, ClipboardList, LogOut, ChevronLeft,
  ChevronRight, Globe, BookOpen, Menu, X, Layers, UserCircle
} from 'lucide-react';
import { Button } from './ui/button';

const menuItems = [
  { href: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
  { href: '/admin/leads', icon: ClipboardList, label: 'Leads' },
  { href: '/admin/companies', icon: Building2, label: 'Firmalar' },
  { href: '/admin/services', icon: Wrench, label: 'Hizmetler' },
  { href: '/admin/cities', icon: MapPin, label: 'Sehirler' },
  { href: '/admin/landing-pages', icon: Globe, label: 'Landing Pages' },
  { href: '/admin/templates', icon: Layers, label: 'Sablonlar' },
  { href: '/admin/directory', icon: Building2, label: 'Sanayi Rehberi' },
  { href: '/admin/blog', icon: PenTool, label: 'Blog' },
  { href: '/admin/cms', icon: FileText, label: 'Icerik (CMS)' },
  { href: '/admin/media', icon: Image, label: 'Medya' },
  { href: '/admin/users', icon: Users, label: 'Kullanicilar' },
  { href: '/admin/audit-logs', icon: BookOpen, label: 'Islem Kaydi' },
  { href: '/admin/settings', icon: Settings, label: 'Ayarlar' },
  { href: '/admin/profile', icon: UserCircle, label: 'Profil' },
];

export default function AdminLayout({ children }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const isActive = (href) => {
    if (href === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Desktop Sidebar */}
      <aside className={`hidden lg:flex flex-col admin-sidebar border-r bg-white ${collapsed ? 'w-16' : 'w-60'}`} data-testid="admin-sidebar">
        <div className="p-4 border-b flex items-center justify-between">
          {!collapsed && (
            <Link to="/admin" className="flex items-center gap-2">
              <Layers className="h-5 w-5 text-[hsl(221,83%,22%)]" />
              <span className="font-semibold text-sm">Admin Panel</span>
            </Link>
          )}
          <button onClick={() => setCollapsed(!collapsed)} className="p-1 hover:bg-slate-100 rounded" data-testid="sidebar-toggle">
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
        </div>

        <nav className="flex-1 py-2 overflow-y-auto">
          {menuItems.map(item => (
            <Link
              key={item.href}
              to={item.href}
              className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors mx-2 rounded-md ${
                isActive(item.href)
                  ? 'bg-[hsl(221,83%,22%)] text-white'
                  : 'text-muted-foreground hover:bg-slate-100 hover:text-foreground'
              }`}
              data-testid={`nav-${item.href.split('/').pop()}`}
              title={collapsed ? item.label : undefined}
            >
              <item.icon className="h-4 w-4 flex-shrink-0" />
              {!collapsed && <span>{item.label}</span>}
            </Link>
          ))}
        </nav>

        <div className="border-t p-4">
          {!collapsed && (
            <div className="mb-3">
              <p className="text-xs font-medium truncate">{user?.name || user?.email}</p>
              <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
            </div>
          )}
          <button onClick={handleLogout} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-red-600 w-full" data-testid="logout-btn">
            <LogOut className="h-4 w-4" />
            {!collapsed && <span>Cikis</span>}
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-50 bg-white border-b h-14 flex items-center px-4 justify-between">
        <button onClick={() => setMobileOpen(true)} data-testid="mobile-admin-menu">
          <Menu className="h-5 w-5" />
        </button>
        <span className="font-semibold text-sm">Admin Panel</span>
        <button onClick={handleLogout}><LogOut className="h-5 w-5 text-muted-foreground" /></button>
      </div>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-64 bg-white">
            <div className="p-4 border-b flex items-center justify-between">
              <span className="font-semibold text-sm">Admin Panel</span>
              <button onClick={() => setMobileOpen(false)}><X className="h-5 w-5" /></button>
            </div>
            <nav className="py-2">
              {menuItems.map(item => (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-3 px-4 py-2.5 text-sm ${
                    isActive(item.href) ? 'bg-[hsl(221,83%,22%)] text-white mx-2 rounded-md' : 'text-muted-foreground hover:bg-slate-100 mx-2 rounded-md'
                  }`}
                >
                  <item.icon className="h-4 w-4" />
                  <span>{item.label}</span>
                </Link>
              ))}
            </nav>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 lg:overflow-y-auto">
        <div className="lg:hidden h-14" /> {/* Spacer for mobile header */}
        <div className="p-4 lg:p-6 max-w-screen-2xl">
          {children}
        </div>
      </main>
    </div>
  );
}
